/**
 * Production adapter wiring the React-free ring ports
 * ({@link RingDeps}) to the live engine and the structure-creation
 * choreography. The GPU/engine is a SYSTEM BOUNDARY: this glue is verified by
 * the manual demo + the mandatory HITL human safety review for this slice,
 * not by unit tests — the unit-tested safety core consumes these ports
 * through deterministic fakes.
 *
 * Geometry uses the proven `SDFOperations.applyRing` engine entry point, so
 * the agent ring path cannot diverge geometrically. The shared
 * overlay/release/create wiring lives in {@link makeEngineDeps}; this file
 * holds only the op-specific apply closure and the undo-history label.
 * `innerCm`→lower, `outerCm`→upper is enforced upstream; here the already-mm
 * offsets are passed straight through.
 */
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import type { Environment } from '~/engine/data/Environment';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { useApplyComputedGeometry } from '~/hooks/structure-set/useApplyComputedGeometry';
import { environmentState } from '~/state/atoms/engineAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    makeEngineDeps,
    requireSourceTexture,
    toComputedGeometry,
} from '../core';
import type { ComputedGeometry, Mm } from '../core';
import type { RingDeps } from './pipeline';

/**
 * Returns a factory producing {@link RingDeps} bound to the engine state.
 * Render-time handles are captured because `getOrCreateDestination` needs
 * the live environment.
 */
export function useRingEngineDeps(): () => RingDeps {
    const env = useRecoilValue(environmentState);
    const structureSet = useRecoilValue(currentStructureSetState);
    const applyGeometry = useApplyComputedGeometry();

    return useCallback(
        (): RingDeps =>
            makeEngineDeps(env, structureSet, applyGeometry, {
                label: 'Ring',
                geometry: (env: Environment | null) => ({
                    async applyRing(
                        sourceRoiNumber: number,
                        innerMm: Mm,
                        outerMm: Mm
                    ): Promise<ComputedGeometry> {
                        const src = requireSourceTexture(env, sourceRoiNumber);
                        // `innerMm` is the lower boundary, `outerMm` the upper —
                        // the arg order `SDFOperations.applyRing` expects.
                        const result = await SDFOperations.applyRing(
                            src,
                            innerMm,
                            outerMm
                        );
                        return toComputedGeometry(result);
                    },
                }),
            }),
        [env, structureSet, applyGeometry]
    );
}
